import {
	Box,
	Chip,
	Step,
	StepButton,
	Stepper,
	Tooltip,
	Typography,
} from "@mui/material";
import { ShapeHistoryRow } from "./hooks/utils/useShapeHistory";
import { getQuestionColor, getReferenceColor } from "./styles";

interface Props {
	history: ShapeHistoryRow[];
	// grid cells the two layers resolved to
	questionId: string;
	questionFrom: string;
	referenceQuestionId: string;
	referenceFrom: string;
	viewingId: string;
	onSelect: (esrId: string) => void;
}

export const ShapeTimeline = ({
	history,
	questionId,
	questionFrom,
	referenceQuestionId,
	referenceFrom,
	viewingId,
	onSelect,
}: Props) => {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: "16px",
				mt: "2px",
				mb: "16px",
			}}
		>
			<Typography sx={{ fontSize: 13, color: "text.secondary" }}>
				Select an addendum to view the shapes recorded at that revision.
			</Typography>

			{history.map((row) => {
				const viewingIndex = row.entries.findIndex(
					(e) => e.esr_id === viewingId,
				);

				return (
					<Box
						key={row.question_id}
						sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}
					>
						<Stepper
							nonLinear
							alternativeLabel
							activeStep={viewingIndex}
							sx={{ flex: 1 }}
						>
							{row.entries.map((entry, index) => {
								// Nothing on screen can come from an ESR created after the one
								// being viewed — resolution stops there
								const outsideView = viewingIndex >= 0 && index > viewingIndex;
								const isViewing = entry.esr_id === viewingId;
								const isQuestionSource =
									row.question_id === questionId && entry.esr_id === questionFrom;
								const isReferenceSource =
									row.question_id === referenceQuestionId &&
									entry.esr_id === referenceFrom;

								const role = isQuestionSource
									? "drawn as current"
									: isReferenceSource
										? "drawn as reference"
										: entry.has_shapes
											? "new version made here"
											: "inherits an earlier version";

								// matches the color these shapes are drawn in on the map
								const roleColor = isQuestionSource
									? getQuestionColor(row.question_id)
									: isReferenceSource
										? getReferenceColor(questionId, referenceQuestionId)
										: null;

								return (
									<Step
										key={entry.esr_id}
										sx={{
											opacity: outsideView ? 0.45 : 1,
											// the connector inside a Step is the one entering it, so
											// hiding it breaks the line where the viewed range stops
											"& .MuiStepConnector-line": outsideView
												? { borderColor: "transparent" }
												: { borderColor: "text.primary" },
											// keep the connector from running right up to the chips
											"& .MuiStepLabel-iconContainer": { px: "8px" },
										}}
									>
										<StepButton
											onClick={() => onSelect(entry.esr_id)}
											icon={
												<Tooltip title={`#${entry.esr_id} — ${role}`}>
													<Chip
														size="small"
														// the first chip names the row, saving both a
														// label column and the width of "initial"
														label={index === 0 ? row.question_id : entry.label}
														variant="outlined"
														sx={{
															bgcolor: roleColor
																? `${roleColor}33`
																: entry.has_shapes
																	? "action.hover"
																	: "transparent",
															borderColor: roleColor ?? "divider",
															borderWidth: roleColor ? 3 : 1,
															fontWeight: isViewing ? 700 : 400,
															boxShadow: isViewing
																? "0 0 0 3px rgba(0, 0, 0, 0.12)"
																: undefined,
														}}
													/>
												</Tooltip>
											}
										/>
									</Step>
								);
							})}
						</Stepper>
					</Box>
				);
			})}
		</Box>
	);
};